import { EventEmitter, Injectable, inject, signal } from '@angular/core';
import { EventType, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { CompanionRole } from '../components/companion/companion.enum';
import { CompanionJobResponse } from '../components/companion/companion.interface';
import { Facility } from '../interfaces/facility';
import { Job } from '../interfaces/job';
import { JobPosting } from '../interfaces/job-posting';
import { TalentpoolFilterService } from './filter/talentpool-filter.service';

@Injectable({
  providedIn: 'root',
})
export class CompanionService {
  private static readonly DRAFT_JOB_EDIT_ROUTE = /\/jobs\/[^/]+\/edit$/;
  private readonly COMPANION_LOCAL_STORAGE_KEY = 'companion-open';
  private readonly talentpoolFilterService = inject(TalentpoolFilterService);

  public role = signal<CompanionRole>(CompanionRole.SUPPORT);
  public talentpoolSearchActive = signal<boolean>(false);
  public companionOpen = signal<boolean>(
    localStorage.getItem(this.COMPANION_LOCAL_STORAGE_KEY) === 'true',
  );

  public router = inject(Router);

  // Job offer creation specific
  public jobOfferChange = new EventEmitter<CompanionJobResponse>();
  public atsJobLoad = new EventEmitter<JobPosting>();
  public templateJobLoad = new EventEmitter<Job>();
  public templateJobLoaded = new EventEmitter<CompanionJobResponse>();
  public lastCompanionJobPayload = signal<CompanionJobResponse>({
    general: {
      jobTitle: null,
      typeOfEmployment: null,
      workingTime: null,
      workExperience: null,
      careerLevel: null,
      fieldOfActivity: null,
      occupations: [],
      startAsSoonAsPossible: true,
      startDate: null,
      salaryMin: null,
      salaryMax: null,
      salaryInterval: null,
    },
    facilities: {
      ids: [],
      remote: null,
    },
    skills: {
      languages: [],
      skills: [],
    },
    description: '',
    completed: false,
  });
  public currentCompanionJobPayload = signal<CompanionJobResponse>(null);
  public addedFacilities = signal<Partial<Facility>[]>([]);
  public sendMessage = new EventEmitter<string>();
  public newChat = new EventEmitter<void>();
  /** Opens the shared ATS load modal hosted by the companion sidebar. */
  public openLoadJobFromAts = new EventEmitter<void>();
  /** Opens the shared PDF load modal hosted by the companion sidebar. */
  public openLoadJobFromPdf = new EventEmitter<void>();
  /** Opens the shared template load modal hosted by the companion sidebar. */
  public openLoadJobFromTemplate = new EventEmitter<void>();

  /** Emitted when the user leaves the job-offer creation context (route change). */
  public leftJobOfferCreation = new EventEmitter<void>();

  readonly draftJobEditMode = signal(false);

  /** True while the job-creation prompting modal is open. */
  readonly promptingModalOpen = signal(false);

  /** True while a PDF is uploaded and extracted inside the prompting modal. */
  readonly promptingAnalyzing = signal(false);

  /** True while a load (PDF/ATS/template/URL) is in progress inside the prompting modal. */
  readonly promptingLoadInProgress = signal(false);

  /** True while the prompting modal shows the generating bridge animation. */
  readonly promptingGenerating = signal(false);

  /**
   * Opens the job prompting modal phase (sidebar input disabled).
   * @returns {void}
   */
  openPromptingModal(): void {
    this.promptingModalOpen.set(true);
    this.promptingAnalyzing.set(false);
    this.promptingLoadInProgress.set(false);
    this.promptingGenerating.set(false);
    this.openCompanion(false);
  }

  /**
   * Closes the job prompting modal phase.
   * @returns {void}
   */
  closePromptingModal(): void {
    this.promptingModalOpen.set(false);
    this.promptingAnalyzing.set(false);
    this.promptingLoadInProgress.set(false);
    this.promptingGenerating.set(false);
  }

  constructor() {
    if (localStorage.getItem(this.COMPANION_LOCAL_STORAGE_KEY) === null) {
      this.openCompanion();
    }

    this.syncCompanionWithRoute(this.router.url);
    this.router.events
      .pipe(filter((event) => event.type === EventType.NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.syncCompanionWithRoute(event.urlAfterRedirects);
      });
  }

  setDraftJobEditMode(
    active: boolean,
    payload?: CompanionJobResponse | null,
  ): void {
    this.draftJobEditMode.set(active);
    if (active && payload) {
      this.lastCompanionJobPayload.set(payload);
      this.currentCompanionJobPayload.set(payload);
    }
    this.applyCompanionState(this.normalizePath(this.router.url));
  }

  getMessageContextRoute(url = this.router.url): string {
    const path = this.normalizePath(url);
    return this.draftJobEditMode() &&
      CompanionService.DRAFT_JOB_EDIT_ROUTE.test(path)
      ? '/jobs/new'
      : path;
  }

  private syncCompanionWithRoute(url: string): void {
    this.draftJobEditMode.set(false);
    this.applyCompanionState(this.normalizePath(url));
  }

  private applyCompanionState(path: string): void {
    const wasJobOfferCreation =
      this.role() === CompanionRole.JOB_OFFER_CREATION;
    const isJobOfferCreation =
      path.endsWith('/jobs/new') ||
      (CompanionService.DRAFT_JOB_EDIT_ROUTE.test(path) &&
        this.draftJobEditMode());
    const isTalentpoolSearch = path.includes('/talents');

    if (wasJobOfferCreation && !isJobOfferCreation) {
      this.leaveJobOfferCreationContext();
    }

    if (isJobOfferCreation) {
      this.role.set(CompanionRole.JOB_OFFER_CREATION);
    } else if (isTalentpoolSearch) {
      this.role.set(CompanionRole.TALENTPOOL_SEARCH);
    } else {
      this.role.set(CompanionRole.SUPPORT);
      this.talentpoolSearchActive.set(false);
    }

    if (isJobOfferCreation) {
      this.openCompanion(false);
      return;
    }

    if (localStorage.getItem(this.COMPANION_LOCAL_STORAGE_KEY) !== 'true') {
      this.closeCompanion();
    }
  }

  /**
   * Clears prompting/job-creation UI state after leaving the job editor.
   * @returns {void}
   */
  private leaveJobOfferCreationContext(): void {
    this.closePromptingModal();
    this.currentCompanionJobPayload.set(null);
    this.lastCompanionJobPayload.set({
      general: {
        jobTitle: null,
        typeOfEmployment: null,
        workingTime: null,
        workExperience: null,
        careerLevel: null,
        fieldOfActivity: null,
        occupations: [],
        startAsSoonAsPossible: true,
        startDate: null,
        salaryMin: null,
        salaryMax: null,
        salaryInterval: null,
      },
      facilities: {
        ids: [],
        remote: null,
      },
      skills: {
        languages: [],
        skills: [],
      },
      description: '',
      completed: false,
    });
    this.addedFacilities.set([]);
    this.leftJobOfferCreation.emit();
  }

  private normalizePath(url: string): string {
    return url.split(/[?#]/)[0].replace(/\/$/, '');
  }

  openCompanion(persistent = true): void {
    this.companionOpen.set(true);
    if (persistent) {
      localStorage.setItem(this.COMPANION_LOCAL_STORAGE_KEY, 'true');
    }
  }

  closeCompanion(): void {
    this.companionOpen.set(false);
    localStorage.setItem(this.COMPANION_LOCAL_STORAGE_KEY, 'false');
  }

  toggleCompanion(): boolean {
    if (this.companionOpen()) {
      this.closeCompanion();
      return false;
    }
    this.openCompanion();
    return true;
  }

  askKira(query: string): void {
    const message = query.trim();
    if (!message) return;
    this.openCompanion();
    this.sendMessage.emit(message);
  }

  startTalentpoolSearch(): void {
    this.talentpoolFilterService.resetFilters();
    this.talentpoolSearchActive.set(true);
    this.role.set(CompanionRole.TALENTPOOL_SEARCH);
    this.openCompanion();
    this.newChat.emit();
    this.sendMessage.emit('<talentpool_search_start>');
  }
}
